import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useBidding } from 'src/models/bids/BidFull/hooks';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faPen } from '@fortawesome/pro-light-svg-icons';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import {
  AssignToQuestion,
  EditableText,
  TaskFulfillmentPicker,
  TaskRequirementPicker,
  TaskResponsePicker,
  TaskStatusPicker
} from 'src/shared';
import { Button, Checkbox, ConditionalWrapper, Tooltip, confirm } from 'src/common';
import classNames from 'classnames';
import { useClickAway, useKey } from 'react-use';
import { IBoxFieldString } from 'src/models/procurements/Tender/types';
import { BoxFieldString } from 'src/models/procurements';
import modalStyles from 'src/common/Modal/index.module.scss';
import { generateTaskUrl, useDeleteBidTasks, useUpdateBidTasks } from 'src/models/bids/BidTask/hooks';
import { Link, useParams } from 'react-router-dom';
import { trackBTOpenDetailedTaskView } from 'src/segment/events';
import { BidTask } from 'src/models/bids/BidTask';
import { BidQuestionType } from 'src/models/bids/BidFull/types';

interface Props {
  task: BidTask;
  type: BidQuestionType;
  selectedTasks: BidTask[];
  onChangeSelection: (taskId: string, checked: boolean) => void;
}

export const Task: FC<Props> = ({ task, type, selectedTasks, onChangeSelection }) => {
  const { id: taskId, groupId } = task;
  const { t } = useTranslation();
  const { data } = useBidding();
  const wsId = data?.workspaceId;
  const { id: bidId } = useParams<{ id: string }>();

  const [isEditing, setEditing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [updateTasks] = useUpdateBidTasks();

  const enterEditing = useCallback(e => {
    e.preventDefault();
    setEditing(() => true);
  }, []);
  const exitEditing = useCallback(() => {
    setEditing(() => false);
  }, []);
  useKey('Escape', () => exitEditing());
  useClickAway(ref, e => {
    e.stopPropagation();
    e.preventDefault();
    exitEditing();
  });
  const onChange = useCallback(
    (value: IBoxFieldString) => {
      taskId &&
        updateTasks({
          questions: [task],
          groupId,
          title: value.string ?? ''
        });
      exitEditing();
    },
    [exitEditing, groupId, task, taskId, updateTasks]
  );

  const [deleteTasks] = useDeleteBidTasks();

  const onDeleteTask = useCallback(() => {
    confirm.confirm({
      title: t('BiddingTool.deleteTask'),
      okText: t('Common.delete'),
      content: t('BiddingTool.deleteTaskWarning'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onOk() {
        deleteTasks({
          questionIds: [taskId],
          groupId,
          type,
          mode: 'single'
        });
      }
    });
  }, [type, t, deleteTasks, taskId, groupId]);
  const onClickTaskLink = useCallback(() => {
    trackBTOpenDetailedTaskView({ taskId, groupId, bidId, taskType: type, eventSource: 'BiddingToolPage' });
  }, [bidId, groupId, taskId, type]);
  const taskUrl = generateTaskUrl(taskId);

  const isTaskSelected = useMemo(() => !!selectedTasks.find(item => item.id === task.id), [selectedTasks, task.id]);

  return task ? (
    <div className={classNames(styles.task, { [styles.isActive]: isTaskSelected })}>
      <Checkbox
        checked={isTaskSelected}
        onChange={e => onChangeSelection(task.id, e.target.checked)}
        className={styles.checkbox}
      />
      <ConditionalWrapper
        condition={!isEditing}
        wrapper={children => (
          <Link className={styles.title} to={taskUrl ?? ''} onClick={onClickTaskLink}>
            {children}
          </Link>
        )}
      >
        {isEditing ? (
          <EditableText
            className={classNames(styles.title, styles.isEditing)}
            containerRef={ref}
            onChange={onChange}
            onFinish={exitEditing}
            field={new BoxFieldString(task.title ?? '', task.title)}
            autoSize={true}
            autoFocus
          />
        ) : (
          <div className={styles.title}>
            <div className={classNames(styles.label, { [styles.isEmpty]: !task.title })}>
              {!!task.title ? <Tooltip title={task.title}>{task.title}</Tooltip> : t('BiddingTool.newTaskPlaceholder')}
            </div>
            {isTaskSelected || !!selectedTasks.length ? null : (
              <Button
                className={styles.editButton}
                type={'text'}
                onClick={enterEditing}
                icon={<FontAwesomeIcon icon={faPen} className={styles.icon} />}
              />
            )}
          </div>
        )}
      </ConditionalWrapper>
      <Button
        className={classNames(styles.comment, { [styles.isActive]: !!task.comments.length })}
        type={'text'}
        icon={<FontAwesomeIcon icon={faComment} />}
      />
      <div className={styles.fulfillment}>
        <TaskFulfillmentPicker
          groupId={groupId}
          questions={[task]}
          taskValue={task.fulfilment}
          type={type}
          disabled={isTaskSelected || !!selectedTasks.length}
          mode="single"
        />
      </div>
      <div className={styles.requirementType}>
        <TaskRequirementPicker
          groupId={groupId}
          questions={[task]}
          taskValue={task.requirementType}
          disabled={isTaskSelected || !!selectedTasks.length}
        />
      </div>
      <div className={styles.responseType}>
        <TaskResponsePicker
          groupId={groupId}
          questions={[task]}
          taskValue={task.responseType}
          type={type}
          disabled={isTaskSelected || !!selectedTasks.length}
          mode="single"
        />
      </div>
      <div className={styles.status}>
        <TaskStatusPicker
          groupId={groupId}
          questions={[task]}
          taskValue={task.status}
          type={type}
          disabled={isTaskSelected || !!selectedTasks.length}
          mode="single"
        />
      </div>
      {bidId && (
        <div className={styles.assignedTo}>
          <AssignToQuestion
            assignedTo={task.assignedTo}
            questions={[task]}
            type={type}
            wsId={wsId}
            groupId={groupId}
            bidId={bidId}
            disabled={isTaskSelected || !!selectedTasks.length}
            assignToMode="single"
          />
        </div>
      )}
      <Tooltip title={t('BiddingTool.deleteTask')}>
        <Button
          className={classNames(styles.delete, { [styles.isHidden]: isTaskSelected || !!selectedTasks.length })}
          type={'text'}
          icon={<FontAwesomeIcon icon={faTrash} onClick={onDeleteTask} />}
          disabled={isTaskSelected || !!selectedTasks.length}
        />
      </Tooltip>
    </div>
  ) : null;
};

export default Task;
